//we will need to clear this store on logout to remove persisted data from local storage

import { create,  } from "zustand";
import { UserData } from 'core/types';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { STORAGE_CONSTANTS, STORAGE_KEY_CONSTANTS, STORAGE_TYPE } from "../storage/constant";
import { getStorage, setStorage, clearParticularKeyStorage } from "@adapter/web-storage";
import { UserAttributes } from "./UserData/types";
const isProductionEnviorment = process.env.NODE_ENV === 'production';

export type UserDataState = {
  userData: UserData | null;
  userAttributes: UserAttributes | null;
  isUserDataLoading: boolean;
  isUserDataError: boolean;
  userDataError: null | unknown;
  isStoreHydrating: boolean;
  isMaskedDataStored: boolean;
  lastApiTriggerTimeStamp: number | null;
  setIsHydrating: (isStoreHydrating: boolean) => void;
};

export const initialState: Omit<UserDataState, 'setIsHydrating'> = {
  userData: null,
  userAttributes: null,
  isUserDataLoading: false,
  isUserDataError: false,
  userDataError: null,
  isStoreHydrating: true,
  isMaskedDataStored: false,
  lastApiTriggerTimeStamp: null, // this will help us to know when the last time data was fetched
};
const isEncrypted = true;
const LocalStorageDataVersion = 0.1; //version of the local storage key
const thirtyDaysMinusTenMinutes =
  STORAGE_CONSTANTS.THIRTY_DAYS_IN_MINS - STORAGE_CONSTANTS.TEN_MINUTES;

const customLocalStorageConfig = {
    getItem: (name: string): string | null => {
        try {
            return getStorage(STORAGE_TYPE.LOCAL_STORAGE, name, isEncrypted);
        } catch (err) {
            console.error('❌ Error reading from localStorage', err);
            return null;
        }
    },
      setItem: (name: string, value: string): void => {
    try {
      if (typeof value === 'string') {
        const parsedValue = JSON.parse(value);

        //Persisting data to local storage only if the API fetch user data successfully (i.e., user data is returned and the api request does not fail).
        const { userData, isUserDataLoading, isUserDataError } =
          parsedValue?.state;

        if (userData && !isUserDataLoading && !isUserDataError) {
          setStorage(
            STORAGE_TYPE.LOCAL_STORAGE,
            name,
            value,
            isEncrypted,
            thirtyDaysMinusTenMinutes
          );
        }
      }
    } catch (err) {
      console.error('❌ Error writing to localStorage', err);
    }
  },

  removeItem: (name: string): void => {
    try {
      clearParticularKeyStorage(STORAGE_TYPE.LOCAL_STORAGE, name);
    } catch (err) {
      console.error('❌ Error removing from localStorage', err);
    }
  },
}

//use clearUserDataStore action to clear the store
export const userDataStore = create<UserDataState>()(
  persist(
    !isProductionEnviorment
      ? devtools(
          set => ({
            ...initialState,
            setIsHydrating: (isStoreHydrating: boolean) =>
              set({ isStoreHydrating }),
          }),
          { name: 'userDataStore' }
        )
      : set => ({
          ...initialState,
          setIsHydrating: (isStoreHydrating: boolean) =>
            set({ isStoreHydrating }),
        }),
    {
      name: STORAGE_KEY_CONSTANTS.USER_DATA_PERSIST_BY_ZUSTAND, //name of local storage key which refer stored user data in local storage
      version: LocalStorageDataVersion,
      storage: createJSONStorage(() => customLocalStorageConfig),

      onRehydrateStorage: () => state => {
        state?.setIsHydrating(false);
      },

      // Optional: You can define migrations here if needed
      // check documentation for more details
      // https://zustand.docs.pmnd.rs/middlewares/persist#persisting-a-state-through-versioning-and-migrations
    }
  )
);